import { XMarkIcon } from '@heroicons/react/24/solid';
import { HTMLAttributes } from 'react';
import { cn } from '@/utils/cn';
import LivescoreFavoriteToggler from '../LivescoreFavoriteToggler/LivescoreFavoriteToggler';
import { Team, useLivescoreLayout } from '@/context/LivescoreLayoutContext';
import LivescoreFavoriteCircleToggler from '../LivescoreFavoriteCircleToggler/LivescoreFavoriteCircleToggler';

function toPolishGender(gender: string) {
  if (gender === 'male') return 'Mężczyzni';
  if (gender === 'female') return 'Kobiety';
  return null;
}

type CompetitorElementProps = HTMLAttributes<HTMLDivElement> & {
  team: Team;
  icon?: 'star' | 'x' | 'circle';
  size?: 'small' | 'large';
  highlight?: boolean;
  addLink?: boolean;
};

export const CompetitorElement = ({
  team,
  className,
  addLink = false,
  icon = 'star',
  size = 'large',
  highlight,
  ...props
}: CompetitorElementProps) => {
  const { favoriteCompetitors, toggleFavoriteCompetitors } = useLivescoreLayout();

  return (
    <div
      className={cn(
        'p-2 flex justify-between items-center transition-colors',
        favoriteCompetitors.has(team.id) &&
          highlight &&
          'dark:bg-green-900 bg-primary-150 dark:!border-green-600 !border-primary-300',
        className,
      )}
      {...props}
    >
      <div className="flex items-center gap-3 w-full relative">
        {addLink ? <a className="absolute inset-0" href={`/livescore/team/${team.slug ?? team.id}`} /> : null}
        <div className={cn('flex items-center justify-center', size === 'small' ? 'w-8 h-8' : 'w-4 h-4')}>
          {team.logo_url ? (
            <img
              src={team.logo_url}
              alt={team.name}
              className={cn('object-contain', size === 'small' ? 'w-8 h-8' : 'w-4 h-4')}
            />
          ) : (
            <div
              className={cn(
                'bg-green-400 rounded-full flex items-center justify-center overflow-hidden',
                size === 'small' ? 'w-8 h-8' : 'w-4 h-4',
              )}
            >
              <span className={cn('font-bold !text-gray-800', size === 'small' ? 'text-xxs' : 'text-[6px]')}>
                {team.abbreviation}
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-col">
          <span className="text-xxs -mb-0.5 text-gray-400">{toPolishGender(team.gender)}</span>
          <p className="text-[12px] dark:text-white text-black font-medium leading-tight">
            {team.name} {team.age_group}
          </p>
        </div>
      </div>
      {icon === 'star' ? (
        <LivescoreFavoriteToggler
          id={team.id}
          isFavorite={favoriteCompetitors.has(team.id)}
          onClick={() => toggleFavoriteCompetitors(team.id)}
        />
      ) : icon === 'circle' ? (
        <LivescoreFavoriteCircleToggler
          id={team.id}
          isFavorite={favoriteCompetitors.has(team.id)}
          onClick={() => toggleFavoriteCompetitors(team.id)}
        />
      ) : (
        <button onClick={() => toggleFavoriteCompetitors(team.id)}>
          <XMarkIcon className="w-[26px] h-[26px] p-1 text-gray-400" />
        </button>
      )}
    </div>
  );
};

export default CompetitorElement;
